import React, { useState } from "react";
import Plot from "react-plotly.js";
import { Loader2Icon, AlertCircleIcon, InfoIcon } from "lucide-react";
import { Layout, ScatterData } from "plotly.js";
import RequestViewModal from "../Request/RequestViewModal";
import { usePermissions } from "../../PermissionsProvider";
import { cn } from "../../lib/utils";

// Legacy interface for backward compatibility
export interface ChartData {
  data: Array<ScatterData>;
  layout: Partial<Layout>;
  status?: "pending" | "generated";
}

interface SemanticChartProps {
  data: ChartData | null;
  loading: boolean;
  error: string | null;
}

export const SemanticChart: React.FC<SemanticChartProps> = ({
  data: figure,
  loading,
  error,
}) => {
  const { apiKey } = usePermissions();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<{
    requestId: string;
    applicationId: string;
    applicationActionId: string;
  } | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-background rounded-lg border border-border">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="w-5 h-5 animate-spin" />
          <span className="text-sm font-gc-reg">Loading chart...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 bg-background rounded-lg border border-border">
        <div className="flex items-center gap-3 text-center max-w-md">
          <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-gc-med text-foreground mb-1">Error Loading Chart</h4>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!figure) {
    return (
      <div className="flex items-center justify-center h-96 bg-background rounded-lg border border-border">
        <div className="flex items-center gap-3 text-center max-w-md">
          <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-gc-med text-foreground mb-1">No Data Available</h4>
            <p className="text-sm text-muted-foreground">There is no data to display in the chart</p>
          </div>
        </div>
      </div>
    );
  }

  if (figure.status === "pending") {
    return (
      <div className="flex items-center justify-center h-96 bg-background rounded-lg border border-border">
        <div className="flex items-center gap-3 text-center max-w-md">
          <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-gc-med text-foreground mb-1">Chart is being generated</h4>
            <p className="text-sm text-muted-foreground">Please check back soon</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background rounded-lg border border-border p-4 h-96 flex items-center justify-center">
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%", height: "100%" }}
        config={{
          responsive: true,
          displayModeBar: false,
        }}
        onClick={(event: any) => {
          const point = event.points[0];
          if (!point) return;

          // customdata contains [requestId, applicationId, applicationActionId, intent, summary, tags]
          const [requestId, applicationId, applicationActionId] =
            point.customdata || [];
          if (requestId && applicationId && applicationActionId) {
            setSelectedRequest({
              requestId,
              applicationId: applicationId.toString(),
              applicationActionId: applicationActionId.toString(),
            });
            setIsModalVisible(true);
          }
        }}
      />
      {selectedRequest && (
        <RequestViewModal
          requestId={selectedRequest.requestId}
          applicationId={selectedRequest.applicationId}
          applicationActionId={selectedRequest.applicationActionId}
          isVisible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedRequest(null);
          }}
          apiKey={apiKey}
        />
      )}
    </div>
  );
};
